import { Link } from 'react-router-dom';
import type { SearchHit } from '@nk/shared';
import { useLocale } from '@/i18n/locale-context';
import { formatDate } from '@/lib/format';
import { ArticleImage } from '@/components/content';
import { Badge, EmptyState, ErrorState, Pagination } from '@/components/ui';
import { ListSkeleton } from '@/routes/page-parts';

/**
 * The result list — PRD §15's "highlighted match snippets".
 *
 * The `<mark>` markup comes from the API (Phase 6 escapes the text and *then* inserts
 * the marks, which is why rendering it here is safe). Doing the highlighting on the
 * client instead would mean re-implementing the same term parsing the server already did
 * against the same stopword list, and getting a different answer whenever they drifted.
 */
export function SearchResults({
  query,
  enabled,
  hits,
  total,
  page,
  perPage,
  isPending,
  isError,
  onRetry,
  onPage,
}: {
  query: string;
  enabled: boolean;
  hits: SearchHit[];
  total: number;
  page: number;
  perPage: number;
  isPending: boolean;
  isError: boolean;
  onRetry: () => void;
  onPage: (page: number) => void;
}) {
  const { locale, t, path } = useLocale();

  if (!enabled) {
    return <EmptyState title={t('search.promptTitle')} body={t('search.promptBody')} />;
  }

  if (isError) {
    return (
      <ErrorState
        title={t('error.genericTitle')}
        body={t('error.genericBody')}
        retryLabel={t('action.retry')}
        onRetry={onRetry}
      />
    );
  }

  if (isPending && hits.length === 0) {
    return <ListSkeleton rows={6} label={t('status.loading')} />;
  }

  if (hits.length === 0) {
    return (
      <EmptyState
        title={t('search.emptyTitle', { query })}
        body={t('search.emptyBody')}
        action={
          <ul className="flex flex-wrap justify-center gap-2">
            {/* Suggestions, not a dead end: the two lists a reader can always fall back
                to (PRD §15's "empty state with suggestions"). */}
            <li>
              <Link
                to={path('trending')}
                className="rounded-md border border-line px-3 py-1.5 text-xs font-semibold transition hover:border-red hover:text-red"
              >
                {t('section.trending')}
              </Link>
            </li>
            <li>
              <Link
                to={path()}
                className="rounded-md border border-line px-3 py-1.5 text-xs font-semibold transition hover:border-red hover:text-red"
              >
                {t('section.latest')}
              </Link>
            </li>
          </ul>
        }
      />
    );
  }

  const pageCount = Math.max(1, Math.ceil(total / Math.max(1, perPage)));

  return (
    <div>
      <ol className="flex flex-col">
        {hits.map((hit) => (
          <li key={hit.id} className="border-b border-line last:border-b-0">
            <Link to={hit.path} className="group flex gap-4 py-4">
              <ArticleImage
                image={hit.image}
                alt={hit.headline}
                sizes="120px"
                placeholderSeed={hit.category.slug}
                className="w-28 shrink-0 overflow-hidden rounded-md sm:w-32"
              />
              <span className="min-w-0">
                <Badge tone="category" color={hit.category.color}>
                  {hit.category.name}
                </Badge>
                <span
                  className="mt-1.5 block text-lg font-bold text-ink transition group-hover:text-red [&_mark]:bg-red-tint [&_mark]:text-red-dark"
                  // Escaped by the API before the marks were inserted (Phase 6).
                  dangerouslySetInnerHTML={{ __html: hit.headlineHighlighted }}
                />
                {hit.snippet && (
                  <span
                    className="mt-1.5 block text-base text-body [&_mark]:bg-red-tint [&_mark]:text-red-dark"
                    dangerouslySetInnerHTML={{ __html: hit.snippet }}
                  />
                )}
                <span className="mt-2 block text-xs text-muted">
                  {formatDate(hit.publishedAt, locale)} ·{' '}
                  {t('meta.readTime', { minutes: hit.readingMinutes })}
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ol>

      <Pagination
        className="mt-8"
        page={page}
        pageCount={pageCount}
        onChange={onPage}
        labels={{
          previous: t('action.previous'),
          next: t('action.next'),
          status: t('search.title'),
        }}
      />
    </div>
  );
}
